import {
  Edit,
  Archive,
  Unarchive,
  Delete,
  AddCircle as Fulfill,
} from "@mui/icons-material";
import { Box, SxProps } from "@mui/material";
import { useSnackbar } from "notistack";
import { ReactElement } from "react";

import {
  ArchiveLinkShortenerDomainMutation,
  ArchiveLinkShortenerDomainMutationVariables,
  DeleteLinkShortenerDomainRequestMutation,
  DeleteLinkShortenerDomainRequestMutationVariables,
  LinkShortenerDomainStatus,
  UnarchiveLinkShortenerDomainMutation,
  UnarchiveLinkShortenerDomainMutationVariables,
} from "~frontend/__generated__/graphql";
import { InternalLink } from "~frontend/components/Link";
import Table, { RowActions } from "~frontend/components/Table";
import {
  ARCHIVE_LINK_SHORTENER_DOMAIN,
  DELETE_LINK_SHORTENER_DOMAIN_REQUEST,
  LIST_DOMAINS_AND_REQUESTS,
  UNARCHIVE_LINK_SHORTENER_DOMAIN,
} from "~frontend/graphql/domains";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { Domain, DomainOrRequest, DomainRequest } from "./types";

const statusLabels: {
  [S in LinkShortenerDomainStatus]: {
    staffLabel: string;
    nonStaffLabel: string;
  };
} = {
  DNS_ONLY_STACK_CREATING: {
    staffLabel: "DNS Setup In Progress",
    nonStaffLabel: "Setup In Progress",
  },
  DNS_ONLY_STACK_FAILED: {
    staffLabel: "DNS Setup Failed",
    nonStaffLabel: "Setup In Progress",
  },
  DNS_ONLY_STACK_READY: {
    staffLabel: "DNS Setup Ready",
    nonStaffLabel: "Setup In Progress",
  },
  FULL_STACK_CREATING: {
    staffLabel: "Final Setup In Progress",
    nonStaffLabel: "Setup In Progress",
  },
  FULL_STACK_FAILED: {
    staffLabel: "Final Setup Failed",
    nonStaffLabel: "Setup In Progress",
  },
  FULL_STACK_LINK_TEST_FAILED: {
    staffLabel: "Final End-To-End Test Failed",
    nonStaffLabel: "Setup In Progress",
  },
  READY: {
    staffLabel: "Active",
    nonStaffLabel: "Active",
  },
  ARCHIVED: {
    staffLabel: "Archived",
    nonStaffLabel: "Archived",
  },
};

function cellStyle(domain: DomainOrRequest): SxProps {
  if (domain.__typename === "LinkShortenerDomainRequest") {
    return { fontStyle: "italic", color: palette.darkGray };
  }

  return {};
}

export default function ShortlinksTable({
  rows,
  page,
  handleChangePage,
  rowsPerPage,
  handleChangeRowsPerPage,
  totalCount,
  handleEditDomain,
  handleFulfillRequest,
}: {
  rows: DomainOrRequest[];
  page: number;
  handleChangePage: (newPage: number) => void;
  rowsPerPage: number;
  handleChangeRowsPerPage: (newRowsPerPage: number) => void;
  totalCount: number;
  handleEditDomain: (domainId: string) => void;
  handleFulfillRequest: (requestId: string) => void;
}): ReactElement {
  const isStaff = useCurrentUserIsStaff();
  const snackbar = useSnackbar();
  const formatErrorMessage = useErrorMessageFormatter();

  const subaccountScopingEnabled = useFeatureFlag(
    "subaccountLinkShortenerDomainScoping"
  );

  const [archiveDomain] = useMutation<
    ArchiveLinkShortenerDomainMutation,
    ArchiveLinkShortenerDomainMutationVariables
  >(ARCHIVE_LINK_SHORTENER_DOMAIN);

  const [unarchiveDomain] = useMutation<
    UnarchiveLinkShortenerDomainMutation,
    UnarchiveLinkShortenerDomainMutationVariables
  >(UNARCHIVE_LINK_SHORTENER_DOMAIN);

  const [deleteRequest] = useMutation<
    DeleteLinkShortenerDomainRequestMutation,
    DeleteLinkShortenerDomainRequestMutationVariables
  >(DELETE_LINK_SHORTENER_DOMAIN_REQUEST, {
    refetchQueries: [LIST_DOMAINS_AND_REQUESTS],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Error deleting request",
  });

  async function handleArchiveDomain(domainId: string) {
    const { errors } = await archiveDomain({
      variables: { input: { linkShortenerDomainId: domainId } },
    });

    if (errors) {
      snackbar.enqueueSnackbar(
        `Error archiving domain: ${formatErrorMessage(errors)}`,
        {
          variant: "error",
        }
      );
    } else {
      snackbar.enqueueSnackbar("Domain archived");
    }
  }

  async function handleUnarchiveDomain(domainId: string) {
    const { errors } = await unarchiveDomain({
      variables: { input: { linkShortenerDomainId: domainId } },
    });

    if (errors) {
      snackbar.enqueueSnackbar(
        `Error unarchiving domain: ${formatErrorMessage(errors)}`,
        {
          variant: "error",
        }
      );
    } else {
      snackbar.enqueueSnackbar("Domain unarchived");
    }
  }

  function actionsForDomain(
    domainOrRequest: DomainOrRequest
  ): RowActions<DomainOrRequest> {
    if (domainOrRequest.__typename === "LinkShortenerDomainRequest") {
      const request: DomainRequest = domainOrRequest;
      const actions: RowActions<DomainOrRequest> = [
        {
          id: "delete",
          type: "button",
          icon: <Delete />,
          tooltipLabel: "Delete Request",
          confirm: {
            title: "Delete Request",
            description: `Are you sure you want to delete the request for ${request.domain}?`,
          },
          onClick: async () => {
            await deleteRequest({
              variables: {
                input: { linkShortenerDomainRequestId: request.id },
              },
            });
          },
        },
      ];

      if (isStaff) {
        actions.unshift({
          id: "fulfill",
          type: "button",
          icon: <Fulfill />,
          tooltipLabel: "Fulfill Request",
          onClick: () => {
            handleFulfillRequest(request.id);
          },
        });
      }

      return actions;
    }

    const domain: Domain = domainOrRequest;
    if (isStaff) {
      // Staff can unarchive archived domains, and can edit/archive non-archived
      // domains
      if (domain.status === LinkShortenerDomainStatus.Archived) {
        return [
          {
            id: "edit",
            type: "button",
            icon: <Edit />,
            tooltipLabel: "Edit Domain",
            onClick: () => {
              handleEditDomain(domain.id);
            },
          },
          {
            id: "unarchive",
            type: "button",
            icon: <Unarchive />,
            tooltipLabel: "Unarchive Domain",
            confirm: {
              title: "Unarchive Domain",
              description: `Are you sure you want to unarchive ${domain.domain}?`,
            },
            onClick: async () => {
              await handleUnarchiveDomain(domain.id).catch(console.error);
            },
          },
        ];
      } else {
        return [
          {
            id: "edit",
            type: "button",
            icon: <Edit />,
            tooltipLabel: "Edit Domain",
            onClick: () => {
              handleEditDomain(domain.id);
            },
          },
          {
            id: "archive",
            type: "button",
            icon: <Archive />,
            tooltipLabel: "Archive Domain",
            confirm: {
              title: "Archive Domain",
              description: `
                Are you sure you want to archive ${domain.domain}? Existing links
                will continue to work, but no new links will be shortened with this
                domain. Any subaccounts that are currently using this domain will
                be switched to "Link shortening off". Any script variants that are
                overriding their subaccount default to use this domain will be
                switched back to their subaccount default. This action cannot
                be undone (you can unarchive the domain later, but these settings
                changes will not be reverted).
              `,
            },
            onClick: async () => {
              await handleArchiveDomain(domain.id).catch(console.error);
            },
          },
        ];
      }
    } else {
      // Non-staff users can only edit domains that are ready (to change
      // the fallback URL)
      if (domain.status === LinkShortenerDomainStatus.Ready) {
        return [
          {
            id: "edit",
            type: "button",
            icon: <Edit />,
            tooltipLabel: "Edit Domain",
            onClick: () => {
              handleEditDomain(domain.id);
            },
          },
        ];
      }
    }

    return [];
  }

  return (
    <Table
      columns={[
        {
          key: "domain",
          title: "Domain",
          renderValue: domain => (
            <Box sx={cellStyle(domain)}>{domain.domain}</Box>
          ),
        },
        {
          key: "createdAt",
          title: "Created At",
          align: "left" as const,
          renderValue: domain => (
            <Box sx={cellStyle(domain)}>
              {domain.__typename === "LinkShortenerDomainRequest"
                ? "—"
                : new Date(domain.createdAt).toLocaleString()}
            </Box>
          ),
        },
        {
          key: "status",
          title: "Status",
          align: "left" as const,
          renderValue: domain => (
            <Box sx={cellStyle(domain)}>
              {domain.__typename === "LinkShortenerDomainRequest"
                ? "Requested"
                : statusLabels[domain.status][
                    isStaff ? "staffLabel" : "nonStaffLabel"
                  ]}
            </Box>
          ),
        },
        ...(subaccountScopingEnabled
          ? [
              {
                key: "availableIn",
                title: "Available In",
                align: "left" as const,
                renderValue: (domain: DomainOrRequest) => {
                  if (domain.__typename === "LinkShortenerDomainRequest") {
                    return null;
                  }
                  if (domain.forAllSubaccounts) {
                    return <Box sx={cellStyle(domain)}>All Subaccounts</Box>;
                  }
                  if (domain.subaccounts.length === 0) {
                    return <Box sx={cellStyle(domain)}>—</Box>;
                  }
                  return (
                    <Box sx={cellStyle(domain)}>
                      {domain.subaccounts.map(s => (
                        <Box key={s.id}>
                          <InternalLink to={`/subaccounts/${s.slug}/settings`}>
                            {s.name}
                          </InternalLink>
                        </Box>
                      ))}
                    </Box>
                  );
                },
              },
            ]
          : []),
      ]}
      topAlignRows
      rows={rows}
      rowActions={actionsForDomain}
      emptyState="No Domains"
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
      page={page}
      onChangePage={handleChangePage}
    />
  );
}
